import { useEffect, useId, useState } from 'react'
import ProjectPicker from './ProjectPicker.jsx'
import QuoteImportReviewTable, { QUOTE_REVIEW_FILTERS, matchesQuoteFilter } from './QuoteImportReviewTable.jsx'
import { confirmQuoteImport, parseQuoteFile } from '../services/partRequestApi'

const ACCEPT = '.pdf,.html,.htm,.xlsx,.xls,.docx,.doc,.hwpx,.hwp,.jpg,.jpeg,.png'

function prepareItems(items) {
  return (items || []).map((item, index) => {
    const suggested = item.suggestedRequestQuantity == null ? item.quantity : item.suggestedRequestQuantity
    const numeric = Number(suggested)
    const rounded = Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : 0
    return {
      ...item,
      key: `${item.rowNumber || index + 1}-${index}`,
      matchedPartId: item.matchedPart?.id || '',
      requestedQty: rounded,
      saveAlias: false,
    }
  })
}

export default function QuoteImportModal({ open, onClose, onSubmitted }) {
  const titleId = useId()
  const [step, setStep] = useState('upload')
  const [file, setFile] = useState(null)
  const [projectId, setProjectId] = useState('')
  const [parsed, setParsed] = useState(null)
  const [items, setItems] = useState([])
  const [reviewFilter, setReviewFilter] = useState('all')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setStep('upload')
    setFile(null)
    setProjectId('')
    setParsed(null)
    setItems([])
    setReviewFilter('all')
    setError('')
    setSaving(false)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, saving])

  if (!open) return null

  async function handleParse(event) {
    event.preventDefault()
    if (!file) {
      setError('파일을 선택해 주세요.')
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('파일 크기는 15MB 이하여야 합니다.')
      return
    }
    setSaving(true)
    setError('')
    setStep('parsing')
    try {
      const result = await parseQuoteFile({ file, projectId })
      setParsed(result)
      setItems(prepareItems(result.items))
      setStep(result.status === 'FAILED' ? 'failed' : 'review')
    } catch (err) {
      setError(err.message || '견적서를 분석하지 못했습니다.')
      setStep('upload')
    } finally {
      setSaving(false)
    }
  }

  async function handleConfirm() {
    const included = items.filter((item) => item.includeInRequest)
    if (included.some((item) => item.matchStatus === 'MATCH_REQUIRED' && !item.matchedPartId)) {
      setError('확인이 필요한 품목은 재고를 선택하거나 미등록으로 요청해 주세요.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await confirmQuoteImport({
        projectId: projectId || '',
        originalFileName: parsed?.file?.originalFileName || file?.name || '',
        originalFileType: parsed?.file?.originalFileType || '',
        parseStatus: parsed?.status,
        warnings: parsed?.warnings || [],
        document: parsed?.document || {},
        items: items.map((item) => ({
          vendorCode: item.vendorCode || '',
          manufacturer: item.manufacturer || '',
          model: item.model || '',
          name: item.name || '',
          quantity: item.quantity,
          unit: item.unit || '개',
          specification: item.specification || '',
          matchStatus: item.matchedPartId ? 'MATCHED' : item.matchStatus,
          matchedPartId: item.matchedPartId || '',
          requestedQty: Number(item.requestedQty) || 0,
          includeInRequest: Boolean(item.includeInRequest),
          saveAlias: Boolean(item.saveAlias),
        })),
      })
      onSubmitted()
    } catch (err) {
      setError(err.message || '일괄 요청을 등록하지 못했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const document = parsed?.document || {}

  return (
    <div className="modal-backdrop" onClick={saving ? undefined : onClose} role="presentation">
      <div
        className="modal-panel modal-panel--form modal-panel--wide"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="modal-title">
          견적서로 일괄 요청
        </h2>

        {step === 'upload' ? (
          <form onSubmit={handleParse}>
            <label className="field">
              <span>견적서 파일</span>
              <input
                className="input"
                type="file"
                accept={ACCEPT}
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
            </label>
            <p className="muted">PDF, HTML/HTM, XLSX/XLS, DOCX/DOC, HWPX/HWP, JPG/JPEG/PNG · 최대 15MB · 파일 1개</p>
            <label className="field">
              <span>사용 프로젝트</span>
              <ProjectPicker
                multiple={false}
                activeOnly
                value={projectId}
                onChange={setProjectId}
                placeholder="프로젝트 검색"
                emptyLabel="선택 안 함"
              />
            </label>
            {error ? <p className="form-error">{error}</p> : null}
            <div className="modal-actions">
              <button type="button" className="btn btn--ghost btn--touch" onClick={onClose}>
                닫기
              </button>
              <button type="submit" className="btn btn--primary btn--touch" disabled={saving}>
                분석
              </button>
            </div>
          </form>
        ) : null}

        {step === 'parsing' ? <p className="muted">견적서를 분석하고 재고와 대조하는 중입니다.</p> : null}

        {step === 'failed' ? (
          <>
            <p className="form-error">{parsed?.warnings?.[0] || '견적서에서 품목을 읽지 못했습니다.'}</p>
            {(parsed?.warnings || []).slice(1).map((warning) => (
              <p key={warning} className="muted">{warning}</p>
            ))}
            <div className="modal-actions">
              <button type="button" className="btn btn--ghost btn--touch" onClick={() => setStep('upload')}>
                다시 선택
              </button>
            </div>
          </>
        ) : null}

        {step === 'review' ? (
          <>
            <div className="request-selected">
              <p><span>공급처</span><strong>{document.supplier || '-'}</strong></p>
              <p><span>견적일</span><strong>{document.quoteDate || '-'}</strong></p>
              <p><span>견적번호</span><strong>{document.quoteNumber || '-'}</strong></p>
              <p><span>총액</span><strong>{document.totalAmount == null ? '-' : document.totalAmount.toLocaleString('ko-KR')}</strong></p>
              <p><span>파일명</span><strong>{parsed?.file?.originalFileName || file?.name}</strong></p>
              <p><span>분석</span><strong>{parsed?.status === 'SUCCESS' ? '정상' : '검토 필요'}</strong></p>
            </div>
            {(parsed?.warnings || []).map((warning) => (
              <p key={warning} className="muted">{warning}</p>
            ))}
            <p className="result-count">품목 {items.length.toLocaleString('ko-KR')}개 · 요청 포함 {items.filter((item) => item.includeInRequest).length.toLocaleString('ko-KR')}개</p>
            <div className="segmented" role="group" aria-label="검토 필터">
              {QUOTE_REVIEW_FILTERS.map((item) => {
                const count = items.filter((row) => matchesQuoteFilter(row, item.id)).length
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`segmented__btn ${reviewFilter === item.id ? 'is-active' : ''}`}
                    onClick={() => setReviewFilter(item.id)}
                  >
                    {item.label} {count.toLocaleString('ko-KR')}
                  </button>
                )
              })}
            </div>
            <QuoteImportReviewTable items={items} filter={reviewFilter} onChange={setItems} />
            {error ? <p className="form-error">{error}</p> : null}
            <div className="modal-actions">
              <button type="button" className="btn btn--ghost btn--touch" onClick={() => setStep('upload')} disabled={saving}>
                다시 선택
              </button>
              <button type="button" className="btn btn--primary btn--touch" onClick={handleConfirm} disabled={saving}>
                {saving ? '등록 중...' : '선택 품목 요청'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}
